import { Button } from "../Button"
import { ElementType, forwardRef, PropsWithChildren, ReactNode } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { ButtonProps, ButtonPropsWithRef } from "../Button/types"
import { Link } from "../Link"
import { LinkProps } from "../Link/types"
import { PolymorphicRef } from "../Box"
import { useCalloutContext } from "./CalloutContext"

export function CalloutHeading({ children }: PropsWithChildren<unknown>) {
  return <span className="font-semibold">{children}</span>
}
CalloutHeading.displayName = "Callout.Heading"

export function CalloutContent({ children }: PropsWithChildren<unknown>) {
  if (typeof children === "string") {
    return <p>{children}</p>
  }
  return <div>{children}</div>
}
CalloutContent.displayName = "Callout.Content"

export function CalloutActions({ children }: PropsWithChildren<unknown>) {
  return <div className="flex flex-wrap gap-x-5 gap-y-2 pt-3">{children}</div>
}
CalloutActions.displayName = "Callout.Actions"

const defaultElement = "button"
export const CalloutActionButton: (<E extends ElementType = typeof defaultElement>(
  props: ButtonPropsWithRef<E>,
) => ReactNode | null) & { displayName?: string } = forwardRef(
  <E extends ElementType = typeof defaultElement>(
    { as: Element, children, variation, ...rest }: ButtonProps<E>,
    ref: PolymorphicRef,
  ) => {
    const { variation: calloutVariation } = useCalloutContext()

    const _variation =
      typeof variation === "undefined"
        ? calloutVariation === "primary"
          ? "primary"
          : "outline"
        : variation

    return (
      <Button as={Element || defaultElement} size="sm" variation={_variation} ref={ref} {...rest}>
        {children}
      </Button>
    )
  },
)
CalloutActionButton.displayName = "Callout.ActionButton"

export const CalloutActionLink = forwardRef<HTMLAnchorElement, LinkProps<typeof ReactRouterLink>>(
  ({ children, variation, ...rest }, ref) => {
    const { variation: calloutVariation } = useCalloutContext()

    const _variation =
      typeof variation === "undefined"
        ? calloutVariation === "primary"
          ? "primary"
          : "gray"
        : variation

    return (
      <Link size="sm" variation={_variation} {...rest} ref={ref}>
        {children}
      </Link>
    )
  },
)

CalloutActionLink.displayName = "Callout.ActionLink"
